import { AnswerType } from './Answer';
import { QuestionType } from './Question';
import { CreatedByType, LangEnum } from './Common';
import { ParamsType } from '../services/resonanceApi';
import { Dependency } from './Dependency';
import { RichContentData } from './richContent';

// A section's intro is a regular Content; we only need its rich-text blocks here.
export interface SectionIntroType {
  id: number;
  label: string;
  content: RichContentData;
}

export interface SectionType {
  id: number;
  order: number;
  intro: SectionIntroType | null;
}

export interface QuestionnaireSession {
  questions: QuestionType[];
  is_scored: boolean;
  answers: AnswerType[];
  dependencies: Dependency[];
  history: QuestionType[];
  version: number;
  sections?: SectionType[];
};

export interface QuestionnaireType {
  id: string;
  version: number;
  title: string;
  count_questions: number;
  description: string;
  last_update_by: CreatedByType;
  created_by: CreatedByType;
  status: QuestionnaireStatusEnum;
  is_integral: boolean;
  is_active: boolean;
  keywords: string;
  points: number;
  affected_to_user_roles: UserRole[];
  is_scored: boolean;
}

export interface QuestionnaireDetailsType extends QuestionnaireType {
  questions: QuestionType[];
  sections?: SectionType[];
}

export interface UserRole {
  profile: string;
  role: string;
}

export interface TranslationQuestionnaireType {
  id: string;
  lang: LangEnum;
  title: string;
  description: string;
  questions: TranslationQuestionType[];
}

export interface TranslationQuestionType {
  id: string;
  title: string;
  description: string;
  answers: TranslationAnswerValueType[];
}

export interface TranslationAnswerValueType {
  id: string;
  label: string;
}

export enum QuestionTypeEnum {
  CHECKBOX = 'checkbox',
  SLIDER = 'slider'
}

export enum QuestionnaireStatusEnum {
  INACTIVE = 'inactive',
  ACTIVE = 'active',
  ERROR = 'error'
}
export interface ComponentType {
  id: number,
  questionnaire_id: number,
  content_id: number,
  order: number,
  deadline: string,
  questions_count: number,
  answered_questions_count: number,
  avg_amount_time_to_answer: number,
  avg_amount_time_to_read: number,
  content_label: string,
  questionnaire_title: string,
  sequenceId: number, //this is the usersequence id
  questions: QuestionType[],
  points: number,
  keywords: string,
  sequence_id: number,
  is_done: boolean,
  done_at: string | null,
  is_scored: boolean
}

export type RecurrenceType = 'daily' | 'weekly' | 'fortnightly' | 'monthly' | 'yearly' | null

export interface SequenceType {
  id: number,
  sequence_id: number,
  start_date: string,
  end_date: string,
  label: string,
  status: SequenceStatusEnum,
  recurrence_type?: RecurrenceType,
  subject: SubjectSequenceType,
  components: ComponentType[];
  subjectInfo?: string
  // Subject's role label (e.g. "Enfant"); null when the user is the subject.
  subjectRoleLabel?: string | null
  // Distinguishes several occurrences of one sequence about different subjects
  // (e.g. a parent's fiche per child): firstname when known, else «Enfant 2».
  // Null when the user is the subject.
  subjectDisplayLabel?: string | null
}

export interface SequenceAvailabilityType {
  sequenceId: number,
  isRecurrent: boolean,
  recurrenceType: RecurrenceType,
  duration: number,
  lastDoneAt: string | null,
  nextEligibleAt: string | null,
  isAvailableNow: boolean,
  activeUserSequenceId: number | null,
}

// The API whitelists the occurrence's subject to these two fields (identity stays out of
// the payload — subjectDisplayLabel already carries what the UI needs).
export interface SubjectSequenceType {
  id: number,
  external_id: string | null
}

export enum SequenceStatusEnum {
  READY = 'READY',
  DONE = 'DONE',
  ENDED_UNCOMPLETED = 'ENDED_UNCOMPLETED',
  IN_PROGRESS = 'IN_PROGRESS'
}

export interface SequenceDetailsType {
  contents: SequenceContentType[],
  answers: SequenceAnswerType[],
}

export interface SequenceContentType {
  id: number,
  read_date: string
}

export interface SequenceAnswerType {
  questionnaire_id: number,
  question_id: number,
  question_type_id: number,
  answer_id: number,
  value: number | string,
  is_empty_answer: boolean,
  answer_date: string,
}

export interface getComponentBodyType {
  component: ComponentType;
  nextComponent: ComponentType;
  isLastSequence: Boolean;
  sequence_id: number;
}

export interface setContentReadParamsType extends ParamsType {
  study_id: number,
  user_id: number,
  sequence_id: number,
  content_id: number,
}

export interface setContentReadBodyType {
  version: number,
  read_date: string,
}